import React, { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { LocalTypeahead } from '@/components/common/local-typeahead';
import { SecureField } from '@/components/secure/secure-field';
import { Button, MoneyTextInput, Switch, Text, TextInput } from '@/components/ui';
import { Modal } from '@/components/ui/modal';
import {
	useBankAccounts,
	useCreateBankAccount,
	useDeleteBankAccount,
	useGlCodes,
	useUpdateBankAccount,
} from '@/hooks/use-accounting';
import { BankAccountKind, GlCodeAccountType, type BankAccount, type GlCode } from '@/types/models';
import { Alert } from '@/utils/alert';
import { todayBusinessMs } from '@/utils/date-utils';
import { parseMoneyToCents } from '@/utils/format-money';

const KIND_LABEL: Record<BankAccountKind, string> = {
	[BankAccountKind.Checking]: 'Checking',
	[BankAccountKind.MoneyMarket]: 'Money Market',
	[BankAccountKind.Savings]: 'Savings',
	[BankAccountKind.Cd]: 'CD',
	[BankAccountKind.Other]: 'Other',
};
const KIND_ORDER: BankAccountKind[] = [
	BankAccountKind.Checking, BankAccountKind.MoneyMarket,
	BankAccountKind.Savings, BankAccountKind.Cd, BankAccountKind.Other,
];

interface FormState {
	name: string;
	kind: BankAccountKind;
	institution: string;
	/** Ciphertext (`sf1:...`) for the full account number. Backend never sees plaintext. */
	accountNumberFull: string;
	accountNumberLast4: string;
	/** Ciphertext (`sf1:...`) for the routing number. */
	routingNumberFull: string;
	routingNumberLast4: string;
	openingBalance: string;
	nextCheckNumber: string;
	isDefault: boolean;
	active: boolean;
	glCodeId: string | null;
}

const fromExisting = (b: BankAccount | undefined, defaultIsDefault: boolean): FormState => ({
	name: b?.name ?? '',
	kind: (b?.kind as BankAccountKind) ?? BankAccountKind.Checking,
	institution: b?.institution ?? '',
	accountNumberFull: b?.accountNumberFull ?? '',
	accountNumberLast4: b?.accountNumberLast4 ?? '',
	routingNumberFull: b?.routingNumberFull ?? '',
	routingNumberLast4: b?.routingNumberLast4 ?? '',
	// Opening balance is creation-only — posted as a journal entry, never
	// stored on the record, so there's nothing to prefill when editing.
	openingBalance: '',
	nextCheckNumber: b?.nextCheckNumber != null ? String(b.nextCheckNumber) : '',
	isDefault: b?.isDefault ?? defaultIsDefault,
	active: b?.active ?? true,
	glCodeId: b?.glCodeId ?? null,
});

interface Props {
	mode: 'create' | 'edit';
	associationId: string;
	existing?: BankAccount;
	/** When true (no existing accounts), the create form pre-checks isDefault. */
	defaultIsDefault?: boolean;
	onClose: () => void;
}

/**
 * Single modal that powers create+edit for bank accounts. Replaces the prior
 * inline expanding card on the bank-accounts page (per "no inline forms" rule).
 */
export const BankAccountModal: React.FC<Props> = ({ mode, associationId, existing, defaultIsDefault = false, onClose }) => {
	const [form, setForm] = useState<FormState>(() => fromExisting(existing, defaultIsDefault));
	const create = useCreateBankAccount();
	const update = useUpdateBankAccount();
	const remove = useDeleteBankAccount();

	// BANK-typed gl_codes only — the chart-of-accounts cash buckets, from the
	// single global chart.
	const { data: glResp } = useGlCodes({
		accountType: GlCodeAccountType.Bank,
		activeOnly: true,
		limit: 200,
	});
	// Every BANK gl_code may only back one bank_account per association (1:1
	// invariant enforced by the import auto-create path). Hide gl_codes already
	// claimed by another bank so the user can't introduce a conflict from the
	// modal.
	const { data: banksResp } = useBankAccounts({ associationId, limit: 500 });
	const glOptions = useMemo(() => {
		const rows = (glResp?.data ?? []) as GlCode[];
		const claimed = new Set<string>();
		for (const b of banksResp?.data ?? []) {
			if (b.id !== existing?.id && b.glCodeId) claimed.add(b.glCodeId);
		}
		return rows
			.filter((g) => !claimed.has(g.id))
			.map((g) => ({ id: g.id, label: `${g.code} — ${g.fullPath || g.name}` }));
	}, [glResp, banksResp, existing?.id]);

	useEffect(() => { setForm(fromExisting(existing, defaultIsDefault)); }, [existing, defaultIsDefault]);

	const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

	const isPending = create.isPending || update.isPending || remove.isPending;
	const canSave = useMemo(() => !!form.name.trim(), [form.name]);

	const onSave = () => {
		if (!canSave) return;
		const trimmedNextCheck = form.nextCheckNumber.trim();
		const parsedNextCheck = trimmedNextCheck ? Number.parseInt(trimmedNextCheck, 10) : NaN;
		const body = {
			name: form.name.trim(),
			kind: form.kind,
			institution: form.institution.trim() || undefined,
			// Ciphertext + last4 are written as a pair; backend never derives last4 itself.
			accountNumberFull: form.accountNumberFull || undefined,
			accountNumberLast4: form.accountNumberLast4 || undefined,
			routingNumberFull: form.routingNumberFull || undefined,
			routingNumberLast4: form.routingNumberLast4 || undefined,
			nextCheckNumber: Number.isFinite(parsedNextCheck) && parsedNextCheck >= 0 ? parsedNextCheck : undefined,
			isDefault: form.isDefault,
			active: form.active,
			glCodeId: form.glCodeId ?? undefined,
		};
		if (mode === 'create') {
			// Opening balance is creation-only — sent as input that posts an
			// 'opening' journal entry server-side, never stored on the row.
			const opening = parseMoneyToCents(form.openingBalance);
			create.mutate({
				associationId,
				...body,
				openingBalanceCents: Number.isFinite(opening) ? opening : 0,
				// Business date, not a wall-clock instant — the opening JE posts on
				// the ET business day (UTC-today would be tomorrow after 8 p.m. ET).
				openingBalanceAsOf: Number.isFinite(opening) && opening !== 0 ? todayBusinessMs() : undefined,
			}, {
				onSuccess: onClose,
				onError: (e) => Alert.alert('Create failed', e.message),
			});
		} else if (existing) {
			update.mutate({ id: existing.id, body }, {
				onSuccess: onClose,
				onError: (e) => Alert.alert('Update failed', e.message),
			});
		}
	};

	const onDelete = () => {
		if (!existing) return;
		const ok = Platform.OS === 'web'
			? window.confirm(`Delete bank account "${existing.name}"? This is a soft-delete; transactions referencing it stay intact.`)
			: true;
		if (!ok) return;
		remove.mutate(existing.id, {
			onSuccess: onClose,
			onError: (e) => Alert.alert('Delete failed', e.message),
		});
	};

	return (
		<Modal visible onDismiss={onClose} onSubmit={onSave} contentContainerStyle={styles.modal}>
			<View style={styles.header}>
				<Text style={styles.title}>{mode === 'create' ? 'Add Bank Account' : 'Edit Bank Account'}</Text>
				<Pressable onPress={onClose} hitSlop={10}><Text style={styles.close}>✕</Text></Pressable>
			</View>
			<ScrollView contentContainerStyle={styles.body}>
				<TextInput
					mode="outlined"
					label="Name *"
					value={form.name}
					onChangeText={(v) => set('name', v)}
					placeholder="US Bank Checking"
					placeholderTextColor="#bdbdbd"
				/>
				<View style={styles.kindRow}>
					{KIND_ORDER.map((k) => (
						<Pressable
							key={k}
							onPress={() => set('kind', k)}
							style={[styles.chip, form.kind === k && styles.chipActive]}
						>
							<Text style={[styles.chipLabel, form.kind === k && styles.chipLabelActive]}>
								{KIND_LABEL[k]}
							</Text>
						</Pressable>
					))}
				</View>
				<View>
					<Text style={styles.fieldLabel}>GL code</Text>
					<LocalTypeahead
						items={glOptions}
						toOption={(o) => ({ id: o.id, label: o.label })}
						value={form.glCodeId}
						onChange={(id) => set('glCodeId', id)}
						placeholder="Search BANK chart-of-accounts codes…"
					/>
					<Text style={styles.help}>
						The cash GL code this bank posts to. Only BANK-typed codes are listed; each
						code may back only one bank account per association.
					</Text>
				</View>
				<TextInput
					mode="outlined"
					label="Institution"
					value={form.institution}
					onChangeText={(v) => set('institution', v)}
					placeholder="US Bank"
					placeholderTextColor="#bdbdbd"
				/>
				<SecureField
					label="Account number"
					value={form.accountNumberFull}
					storedMask={form.accountNumberLast4 ? `••••${form.accountNumberLast4}` : null}
					maskStrategy="last4"
					onChange={({ ciphertext, mask }) => {
						setForm((f) => ({
							...f,
							accountNumberFull: ciphertext,
							accountNumberLast4: mask.replace(/\D/g, '').slice(-4),
						}));
					}}
					placeholder="123456789012"
					helperText="Stored encrypted; never visible to the server."
				/>
				<SecureField
					label="Routing number"
					value={form.routingNumberFull}
					storedMask={form.routingNumberLast4 ? `••••${form.routingNumberLast4}` : null}
					maskStrategy="last4"
					onChange={({ ciphertext, mask }) => {
						setForm((f) => ({
							...f,
							routingNumberFull: ciphertext,
							routingNumberLast4: mask.replace(/\D/g, '').slice(-4),
						}));
					}}
					placeholder="026009593"
					helperText="Stored encrypted; never visible to the server."
				/>
				{mode === 'create' && (
					<>
						<MoneyTextInput
							mode="outlined"
							label="Opening balance"
							value={form.openingBalance}
							onChangeText={(v) => set('openingBalance', v)}
							placeholder="0.00"
							placeholderTextColor="#bdbdbd"
						/>
						<Text style={styles.help}>
							Posted as an opening journal entry against this bank&apos;s GL code.
							Requires a GL code, and can only be set at creation — adjust later with a
							manual journal entry.
						</Text>
					</>
				)}
				<TextInput
					mode="outlined"
					label="Next check number"
					value={form.nextCheckNumber}
					onChangeText={(v) => set('nextCheckNumber', v.replace(/\D/g, ''))}
					placeholder="1001"
					placeholderTextColor="#bdbdbd"
					keyboardType="number-pad"
				/>
				<Text style={styles.help}>
					Auto-increments each time a check is printed from this account. Set this to match your next physical check.
				</Text>
				<View style={styles.switchRow}>
					<Switch value={form.isDefault} onValueChange={(v) => set('isDefault', v)} />
					<Text>Default account for this association</Text>
				</View>
				<View style={styles.switchRow}>
					<Switch value={form.active} onValueChange={(v) => set('active', v)} />
					<Text>Active</Text>
				</View>
			</ScrollView>

			<View style={styles.footer}>
				{mode === 'edit' && (
					<Button mode="outlined" onPress={onDelete} textColor="#c62828" disabled={isPending}>
						Delete
					</Button>
				)}
				<View style={{ flex: 1 }} />
				<Button mode="outlined" onPress={onClose} disabled={isPending}>Cancel</Button>
				<Button mode="contained" onPress={onSave} disabled={!canSave || isPending}>
					{mode === 'create' ? 'Add' : 'Save'}
				</Button>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	modal: { backgroundColor: '#fff', borderRadius: 8, margin: 16, maxWidth: 560, alignSelf: 'center', width: '92%' },
	header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottomWidth: 1, borderBottomColor: '#eceff1' },
	title: { fontWeight: '700', fontSize: 16 },
	close: { fontSize: 18, color: '#78909c' },
	body: { padding: 16, gap: 10 },
	footer: { flexDirection: 'row', gap: 8, padding: 16, borderTopWidth: 1, borderTopColor: '#eceff1', alignItems: 'center' },
	kindRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
	chip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 14, backgroundColor: '#eceff1' },
	chipActive: { backgroundColor: '#1976d2' },
	chipLabel: { color: '#37474f', fontSize: 12, fontWeight: '500' },
	chipLabelActive: { color: '#fff' },
	switchRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 },
	help: { fontSize: 11, color: '#78909c', fontStyle: 'italic', marginTop: -4 },
	fieldLabel: { fontSize: 11, color: '#546e7a', marginBottom: 4 },
});
